<template>
    <v-app>
        <mainMenu></mainMenu>
        <v-container fluid>
            <v-layout align-center wrap class="mb-2">
                <v-flex xs12 sm4 md3>
                    <v-menu v-model="dateOpen" :close-on-content-click="false" offset-y max-width="290">
                        <template v-slot:activator="{ on }">
                            <v-text-field :value="date" label="วันที่" prepend-icon="mdi-calendar"
                                readonly v-on="on" hide-details color="orange accent-3"></v-text-field>
                        </template>
                        <v-date-picker v-model="date" color="orange accent-3"
                            @input="dateOpen = false"></v-date-picker>
                    </v-menu>
                </v-flex>
                <v-spacer></v-spacer>
                <v-flex xs12 sm4 md3 text-right>
                    <span class="caption grey--text">{{ summary }}</span>
                </v-flex>
            </v-layout>

            <v-tabs v-model="tab" color="orange accent-3" class="mb-2">
                <v-tab>รายการ</v-tab>
                <v-tab>แผนที่</v-tab>
            </v-tabs>

            <v-alert v-if="error" type="error" dense text>{{ error }}</v-alert>

            <v-tabs-items v-model="tab">
                <v-tab-item>
                    <v-progress-linear v-if="loading" indeterminate color="orange accent-3"></v-progress-linear>
                    <v-layout v-if="!loading && !board.length" justify-center class="pa-6">
                        <span class="grey--text">{{ emptyText }}</span>
                    </v-layout>
                    <v-expansion-panels v-model="openPanels" multiple>
                        <v-expansion-panel v-for="truck in board" :key="truck.regNum">
                            <v-expansion-panel-header>
                                <v-layout align-center wrap>
                                    <v-flex xs12 sm3>
                                        <strong>{{ truck.regNum }}</strong>
                                        <span class="grey--text ml-2">{{ truck.driverName }}</span>
                                    </v-flex>
                                    <v-flex xs12 sm3>
                                        <v-chip small :color="stateColor(truck)" text-color="white">
                                            {{ stateText(truck) }}
                                        </v-chip>
                                        <v-chip v-if="truck.failedCount" small color="red" text-color="white" class="ml-1">
                                            ส่งไม่สำเร็จ {{ truck.failedCount }}
                                        </v-chip>
                                        <v-chip v-if="truck.awaitingCloseCount" small color="amber darken-3" text-color="white" class="ml-1">
                                            รอปิดงาน {{ truck.awaitingCloseCount }}
                                        </v-chip>
                                        <v-chip v-if="truck.followUpCount" small color="orange darken-2" text-color="white" class="ml-1">
                                            ติดตาม {{ truck.followUpCount }}
                                        </v-chip>
                                        <v-chip v-if="truck.trip" small color="indigo" text-color="white" class="ml-1">
                                            วันที่ {{ truck.trip.dayNo }}/{{ truck.trip.days }}
                                        </v-chip>
                                        <v-chip v-if="truck.overdueCount" small color="red darken-3" text-color="white" class="ml-1">
                                            ค้างส่ง {{ truck.overdueCount }}
                                        </v-chip>
                                    </v-flex>
                                    <v-flex xs12 sm3>
                                        <v-chip small :color="delayColor(truck.delayLevel)" text-color="white">
                                            {{ delayText(truck) }}
                                        </v-chip>
                                    </v-flex>
                                    <v-flex xs12 sm3>
                                        <span class="caption grey--text">{{ pingText(truck.ping) }}</span>
                                    </v-flex>
                                </v-layout>
                            </v-expansion-panel-header>
                            <v-expansion-panel-content>
                                <div v-if="truck.current" class="mb-2">
                                    กำลังไปที่ {{ truck.current.seq }}. {{ truck.current.custName }}
                                    <div class="caption grey--text">{{ truck.current.custAddress }}</div>
                                </div>
                                <v-simple-table dense>
                                    <thead>
                                        <tr>
                                            <th>ลำดับ</th><th>ลูกค้า</th><th>นัด</th>
                                            <th>คาดถึง</th><th>ถึงจริง</th><th>เสร็จ</th><th>ช้า/เร็ว</th><th>หลักฐาน</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="stop in truck.stops" :key="stop.id">
                                            <td>{{ stop.seq }}</td>
                                            <td>
                                                {{ stop.custName }}
                                                <v-chip v-if="stop.status === 'failed'" x-small color="red" text-color="white" class="ml-1">
                                                    ส่งไม่สำเร็จ
                                                </v-chip>
                                                <!-- Signed for, not closed: the driver still owes the
                                                     receipt photo. Worth a phone call if it lingers. -->
                                                <v-chip v-else-if="isAwaitingClose(stop)" x-small color="amber darken-3"
                                                    text-color="white" class="ml-1">
                                                    รอปิดงาน
                                                </v-chip>
                                                <v-chip v-else-if="stop.status === 'done' && stop.proof && stop.proof.followUp"
                                                    x-small color="orange darken-2" text-color="white" class="ml-1">
                                                    ต้องติดตาม
                                                </v-chip>
                                                <!-- Left at the house with nobody home (the consent
                                                     passed as ลูกค้าไม่อยู่): worth a call to confirm.
                                                     Its own v-if, since it can sit beside any of the above. -->
                                                <v-chip v-if="isAbsent(stop)" x-small color="blue-grey"
                                                    text-color="white" class="ml-1">
                                                    ลูกค้าไม่อยู่
                                                </v-chip>
                                            </td>
                                            <td>{{ stop.promisedTime || '-' }}</td>
                                            <td>{{ stop.etaClock || '-' }}</td>
                                            <td>{{ stop.actualArrive || '-' }}</td>
                                            <td>{{ stop.actualFinish || '-' }}</td>
                                            <td>{{ minutesText(stop.delayMinutes) }}</td>
                                            <td>
                                                <v-btn v-if="stop.proof" icon small color="primary"
                                                    @click="openProof(stop)">
                                                    <v-icon small>{{ proofIcon(stop.proof) }}</v-icon>
                                                </v-btn>
                                                <span v-else>-</span>
                                            </td>
                                        </tr>
                                    </tbody>
                                </v-simple-table>
                            </v-expansion-panel-content>
                        </v-expansion-panel>
                    </v-expansion-panels>
                </v-tab-item>
                <v-tab-item>
                    <!-- v-if, not v-show: Leaflet measures its container on
                         init, and a hidden div measures zero, which leaves a
                         blank map until the window is resized. -->
                    <monitorMap v-if="tab === 1" :board="board"></monitorMap>
                </v-tab-item>
            </v-tabs-items>

            <!-- What the driver left at the customer's home: the photos of each
                 stage in visit order (the house, the room before, the work
                 after), the signature and what the customer said. Staff only --
                 the router keeps shops off this page, and deliveryStop is not
                 something track.vue ever shows a customer. -->
            <v-dialog v-model="proofOpen" max-width="720" scrollable>
                <v-card v-if="proofStop">
                    <v-card-title class="subtitle-1">
                        หลักฐานการส่งงาน · {{ proofStop.custName }}
                    </v-card-title>
                    <v-card-text>
                        <!-- How the visit ended, before the evidence for it. -->
                        <v-alert v-if="proofStop.status === 'failed'" type="error" dense text>
                            ส่งไม่สำเร็จ: {{ rejectText(proofStop.proof) || '-' }}
                        </v-alert>
                        <v-alert v-else-if="proofStop.proof.followUp" type="warning" dense text>
                            ลูกค้ารับสินค้า แต่ต้องติดตามงาน: {{ followUpText(proofStop.proof) || '-' }}
                        </v-alert>
                        <!-- ใบยินยอมรับบริการติดตั้ง, signed before เริ่มติดตั้ง. Shown
                             only once installation was reached or consent saved:
                             a stop still on the road has nothing to show yet. -->
                        <div v-if="consentShown(proofStop)" class="mb-3">
                            <div class="subtitle-2 black--text mb-1">ใบยินยอมรับบริการติดตั้ง</div>
                            <v-img v-if="proofStop.proof.consentSignatureUrl" :src="proofStop.proof.consentSignatureUrl"
                                max-width="360" contain class="grey lighten-4"></v-img>
                            <div v-else-if="proofStop.proof.consentSkippedReason" class="body-2 orange--text text--darken-4">
                                ไม่มีใบยินยอม — {{ proofStop.proof.consentSkippedReason }}
                            </div>
                            <div v-else class="body-2 grey--text text--darken-1">ไม่มีใบยินยอม</div>
                            <div v-if="proofStop.proof.consentSignedName" class="body-2 mt-1">
                                ผู้ลงนาม: {{ proofStop.proof.consentSignedName }}
                            </div>
                            <div v-if="proofStop.proof.consentVersion" class="caption grey--text text--darken-1">
                                {{ consentClock(proofStop.proof) }} · ข้อความฉบับ {{ proofStop.proof.consentVersion }}
                            </div>
                        </div>
                        <div v-for="group in proofGroups" :key="group.step" class="mb-3">
                            <div class="subtitle-2 black--text mb-1">
                                {{ group.title }}
                                <!-- Added by the driver after the job was closed. -->
                                <v-chip v-if="group.late" x-small outlined color="orange darken-3" class="ml-1">เพิ่มภายหลัง</v-chip>
                            </div>
                            <div v-if="group.urls.length" class="d-flex flex-wrap">
                                <a v-for="(url, i) in group.urls" :key="url" :href="url"
                                    target="_blank" rel="noopener" class="mr-2 mb-2">
                                    <v-img :src="url" width="200" height="150" class="grey lighten-3"
                                        :alt="group.short + ' รูปที่ ' + (i + 1)"></v-img>
                                </a>
                            </div>
                            <div v-else class="body-2 grey--text text--darken-1">{{ group.emptyText }}</div>
                        </div>

                        <!-- A stop rejected before the customer's page was saved
                             has no signature to show, and "ยังไม่ปิดงาน" would be
                             wrong: it is closed, as ส่งไม่สำเร็จ. -->
                        <template v-if="proofStop.status !== 'failed' || 'signatureUrl' in proofStop.proof">
                        <div class="subtitle-2 black--text mt-2 mb-1">ลายเซ็นลูกค้า</div>
                        <v-img v-if="proofStop.proof.signatureUrl" :src="proofStop.proof.signatureUrl"
                            max-width="360" contain class="grey lighten-4"></v-img>
                        <!-- The customer's page writes signatureUrl even when it
                             is blank, so its absence means they have not reached
                             it yet -- not that the customer did not sign. -->
                        <div v-else-if="!('signatureUrl' in proofStop.proof)" class="body-2 grey--text text--darken-1">
                            ยังไม่ปิดงาน
                        </div>
                        <div v-else class="body-2 grey--text text--darken-1">
                            ไม่มีลายเซ็น{{ proofStop.proof.refusedReason ? ' — ' + proofStop.proof.refusedReason : '' }}
                        </div>
                        <div v-if="proofStop.proof.signedName" class="body-2 mt-1">
                            ผู้เซ็น: {{ proofStop.proof.signedName }}
                        </div>

                        <div v-if="proofStop.proof.rating" class="d-flex align-center mt-2">
                            <span class="body-2 mr-2">ความพึงพอใจ</span>
                            <v-rating :value="proofStop.proof.rating" readonly dense small
                                color="orange accent-4" background-color="grey lighten-1"></v-rating>
                        </div>
                        <div v-else-if="proofStop.proof.ratingDeclined" class="body-2 mt-2 grey--text text--darken-1">
                            ความพึงพอใจ: ลูกค้าไม่ประสงค์ให้คะแนน
                        </div>
                        <div v-if="proofStop.proof.comment" class="body-2 mt-1">
                            ความเห็น: {{ proofStop.proof.comment }}
                        </div>
                        </template>
                    </v-card-text>
                    <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn text @click="proofOpen = false">ปิด</v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>
        </v-container>
    </v-app>
</template>
<script>
import mainMenu from '@/components/mainMenu'
import monitorMap from '@/components/monitorMap'
import monitorService from '@/components/monitorService'
import F from '@/components/fleetBoard'
import D from '@/components/driverDay'
import P from '@/components/proofPhoto'
import O from '@/components/deliveryOutcome'
import moment from 'moment-timezone'
import C from '@/components/consentForm'

export default {
    components: { mainMenu, monitorMap },
    data () {
        return {
            date: monitorService.todayISO(),
            dateOpen: false,
            tab: 0,
            board: [],
            // What is open is remembered by PLATE, never by position. See
            // openPanels below.
            openPlates: [],
            loading: true,
            error: '',
            proofOpen: false,
            proofStop: null,
            unsubscribe: null
        }
    },
    computed: {
        // One group per photo stage, in visit order, read through the same
        // table the driver app writes with. A stage whose field is absent was
        // never reached -- or the job predates that stage, as every job
        // finished on 1.22.x predates the house and before photos -- which is
        // a different answer from a driver who recorded a reason.
        proofGroups () {
            const proof = (this.proofStop && this.proofStop.proof) || {}
            // The receipt and the rejection evidence are not steps every visit
            // passes through: a failed stop has no receipt, a delivered one no
            // rejection. Listed only when the stop wrote them.
            const stages = P.STAGE_ORDER.filter(step =>
                P.stampStageFor(step) || P.STAGES[step].urlsField in proof)
            const late = P.lateAddedSteps(proof)
            return stages.map(step => {
                const stage = P.STAGES[step]
                const urls = proof[stage.urlsField] || []
                const reason = proof[stage.skipField] || ''
                let emptyText = 'ไม่มีรูป'
                if (!(stage.urlsField in proof)) emptyText = 'ยังไม่มีรูป'
                else if (reason) emptyText = 'ไม่มีรูป — ' + reason
                return { step, title: P.titleFor(step, C.customerAbsent(this.proofStop)), short: stage.short, urls, emptyText, late: late.indexOf(step) > -1 }
            })
        },
        summary () {
            const late = this.board.filter(t => t.delayLevel === 'late' || t.delayLevel === 'veryLate').length
            return this.board.length + ' คัน · ช้า ' + late + ' คัน'
        },
        // Vuetify tracks open panels by INDEX, and buildBoard re-sorts the
        // fleet on every snapshot -- so a single ping that re-ranks the trucks
        // would leave the header the dispatcher opened sitting above a
        // different truck's stops, with nothing to show it had changed.
        // Translating to and from the plate on every read keeps the same truck
        // open across a re-sort.
        openPanels: {
            get () {
                const open = []
                this.board.forEach((truck, i) => {
                    if (this.openPlates.indexOf(truck.regNum) >= 0) open.push(i)
                })
                return open
            },
            set (indexes) {
                this.openPlates = (indexes || [])
                    .map(i => (this.board[i] ? this.board[i].regNum : ''))
                    .filter(plate => plate)
            }
        },
        // A date the user chose is not necessarily today, and "วันนี้" on a
        // past date reads as a bug in the board rather than an empty day.
        emptyText () {
            return this.date === monitorService.todayISO()
                ? 'ไม่มีรถออกงานในวันนี้'
                : 'ไม่มีรถออกงานในวันที่เลือก'
        }
    },
    watch: {
        // Re-pointing the listeners is the whole date change. Tearing the old
        // ones down FIRST is not optional: three listeners per date change would
        // otherwise accumulate for the life of the tab.
        date () { this.startWatching() }
    },
    methods: {
        // The stop is copied at the moment of the tap. The board re-sorts and
        // rebuilds on every snapshot, so holding a live row would let the dialog
        // quietly switch to another customer's proof mid-look.
        openProof (stop) {
            this.proofStop = { custName: stop.custName, status: stop.status, steps: stop.steps || {}, proof: Object.assign({}, stop.proof) }
            this.proofOpen = true
        },
        isAwaitingClose (stop) { return D.awaitingClose(stop) },
        isAbsent (stop) { return C.customerAbsent(stop) },
        // Consent is asked for at เริ่มติดตั้ง, so a stop that never got there
        // (a rejection at the door) and saved none has nothing to show.
        // "ไม่มีใบยินยอม" is for an installation that started without one --
        // every stop installed before this step existed.
        consentShown (stop) {
            const proof = stop.proof || {}
            const steps = stop.steps || {}
            return !!(proof.consentVersion || (steps.installStarted && steps.installStarted.at != null))
        },
        consentClock (proof) {
            const m = proof.consentAtDevice ? moment.tz(proof.consentAtDevice, 'Asia/Bangkok') : null
            return m && m.isValid() ? 'เซ็นเมื่อ ' + m.format('DD/MM HH:mm') : ''
        },
        rejectText (proof) { return O.reasonText(proof.rejectReasons, proof.rejectNote) },
        followUpText (proof) { return O.reasonText(proof.followUpReasons, proof.followUpNote) },
        proofIcon (proof) {
            const anyPhoto = P.STAGE_ORDER.some(step => (proof[P.STAGES[step].urlsField] || []).length)
            if (anyPhoto) return 'mdi-image-multiple'
            return proof.signatureUrl ? 'mdi-draw' : 'mdi-file-document-outline'
        },
        startWatching () {
            this.stopWatching()
            // Yesterday's fleet must not sit under today's header while the new
            // snapshots are in flight -- and if they then fail, it must not sit
            // beside the error alert looking current.
            this.board = []
            this.loading = true
            this.error = ''
            this.unsubscribe = monitorService.watchDay(
                this.date,
                board => { this.board = board; this.loading = false },
                () => { this.error = 'โหลดข้อมูลไม่สำเร็จ กรุณาลองใหม่'; this.loading = false }
            )
        },
        stopWatching () {
            if (this.unsubscribe) { this.unsubscribe(); this.unsubscribe = null }
        },
        stateText (truck) {
            if (truck.state === 'notStarted') return 'ยังไม่ออกงาน'
            if (truck.state === 'finished') return truck.failedCount ? 'ปิดงานครบแล้ว' : 'ส่งครบแล้ว'
            return 'ส่งแล้ว ' + truck.doneCount + '/' + truck.totalCount
        },
        stateColor (truck) {
            if (truck.state === 'notStarted') return 'grey'
            if (truck.state === 'finished') return 'green'
            return 'blue'
        },
        delayText (truck) {
            if (truck.delayLevel === 'unknown') return 'ไม่ทราบเวลา'
            return this.minutesText(truck.delayMinutes)
        },
        delayColor (level) {
            if (level === 'veryLate') return 'red'
            if (level === 'late') return 'orange'
            if (level === 'unknown') return 'grey'
            return 'green'
        },
        minutesText (minutes) {
            if (minutes == null) return '-'
            if (minutes > F.ON_TIME_MINUTES) return 'ช้า ' + minutes + ' นาที'
            if (minutes < 0) return 'เร็ว ' + Math.abs(minutes) + ' นาที'
            return 'ตรงเวลา'
        },
        // A stale dot that looks live is worse than no dot: the driver's phone
        // may simply be in their pocket, which stops a browser reading position
        // at all. The age is always shown, and past STALE_MS it says why.
        pingText (ping) {
            if (!ping || ping.freshness === 'none') return 'ยังไม่มีตำแหน่ง'
            const age = 'อัปเดต ' + ping.ageMinutes + ' นาทีที่แล้ว'
            return ping.freshness === 'stale' ? age + ' — โทรศัพท์อาจล็อกหน้าจอ' : age
        }
    },
    mounted () { this.startWatching() },
    beforeDestroy () { this.stopWatching() }
}
</script>
